import { useMemo, useState } from "react";

/**
 * Custom React hook for managing an editable copy of an entity object.
 *
 * @param {Object} sourceEntity - The original entity object to be edited.
 * @returns {[Object, Function, boolean, Function]} - Returns:
 *   [0] editableEntity: a copy of the entity that can be modified
 *   [1] setEditableEntity: setter function to update the editable entity
 *   [2] isChanged: boolean indicating if the editable entity differs from the source
 *   [3] reset: function to reset the editable entity to the source entity
 *
 * The hook tracks changes between the source and editable entity, including Date fields.
 */
export const useEditableEntity = (sourceEntity) => {
    const [editableEntity, setEditableEntity] = useState({ ...sourceEntity });

    const isChanged = useMemo(() => Object.keys(sourceEntity).reduce((prevResult, currentField) => {
        const notEqual = (sourceEntity[currentField] instanceof Date && editableEntity[currentField] instanceof Date) ? sourceEntity[currentField].getTime() !== editableEntity[currentField].getTime() : sourceEntity[currentField] !== editableEntity[currentField];

        return prevResult || notEqual;
    }, false), [sourceEntity, editableEntity]);

    const reset = () => {
        setEditableEntity(sourceEntity);
    }

    return [editableEntity, setEditableEntity, isChanged, reset];
}